import { useLocalization } from "@fluent/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, MenuButton } from "@narmi/design_system";
import { DisplayStatus } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import type { PaymentCancelAction } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import styles from "../PaymentActivity.module.scss";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import {
  TableWithDrawer,
  RenderDrawerProps,
  PaymentActivityRow,
} from "../common";
import { PAYMENT_ACTIVITY_ROUTE, getReverseFromListRoute } from "../routes";
import usePaymentActions from "./usePaymentActions";
import { PaymentActivityDrawer } from "./";

const PaymentActivityTable = ({
  payments,
  handleToggleCancelDialog,
}: {
  payments: API.FormattedPaymentActivity[];
  handleToggleCancelDialog: (target: PaymentCancelAction) => void;
}) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasDualApprovalPermission, getActions } = usePaymentActions();

  const handleOpenDrawer = (uuid: string) => {
    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${uuid}${location.search}`);
  };

  const renderRow = (item: API.FormattedPaymentActivity) => {
    const { uuid, displayStatus, dualApprovalId } = item;
    const isPendingApproval = displayStatus === DisplayStatus.pending_approval;
    const { showCancelButton, showReverseButton, cancelAction } =
      getActions(item);
    const menuItems = [];

    if (isPendingApproval && dualApprovalId && hasDualApprovalPermission) {
      menuItems.push(
        <MenuButton.Item
          key="view-button"
          label={l10n.getString("label-view-approvals")}
          startIcon="eye"
          onSelect={() =>
            navigate(`/approvals/pending?dualApprovalId=${dualApprovalId}`)
          }
        />
      );
    }

    if (displayStatus === DisplayStatus.in_progress) {
      menuItems.push(
        <MenuButton.Item
          key="view-button"
          label={l10n.getString("label-view-recurring")}
          startIcon="eye"
          onSelect={() => {
            navigate({
              pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
              search: `?${
                FILTER_CONFIG[FilterChips.RECURRING_PAYMENT].queryKey
              }=${uuid}`,
            });
          }}
        />
      );
    }

    if (showReverseButton) {
      menuItems.push(
        <MenuButton.Item
          key="reverse-button"
          label={l10n.getString("label-reverse-payment")}
          startIcon="rotate-ccw"
          onSelect={() =>
            navigate(`${getReverseFromListRoute(uuid)}${location.search}`)
          }
        />
      );
    }

    if (showCancelButton && cancelAction) {
      menuItems.push(
        <MenuButton.Item
          key="delete-button"
          label={l10n.getString("label-cancel-payment")}
          startIcon="trash-2"
          onSelect={() => handleToggleCancelDialog(cancelAction)}
        />
      );
    }

    return (
      <PaymentActivityRow
        key={`table-row__${uuid}`}
        payment={item}
        onRowClick={() => handleOpenDrawer(uuid)}
        actionCell={
          <Table.Cell>
            <div
              className={styles.actionCellContent}
              onClick={(e) => e.stopPropagation()}
            >
              {menuItems.length > 0 && (
                <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
              )}
            </div>
          </Table.Cell>
        }
      />
    );
  };

  const renderDrawer: RenderDrawerProps<API.FormattedPaymentActivity> = (
    payment,
    paymentId,
    { onClose, onNext, onPrev, isOpen }
  ) => (
    <PaymentActivityDrawer
      isOpen={isOpen}
      handleClose={onClose}
      handleOnNext={onNext}
      handleOnPrev={onPrev}
      handleToggleCancelDialog={handleToggleCancelDialog}
      payment={payment}
      paymentUuid={
        paymentId as
          | API.ScheduledACHPaymentId
          | API.ACHPaymentId
          | API.SwiftTransactionId
      }
    />
  );

  return (
    <TableWithDrawer
      items={payments}
      renderRow={renderRow}
      renderDrawer={renderDrawer}
      basePath={PAYMENT_ACTIVITY_ROUTE}
      paramKey="activityUuid"
    />
  );
};

export default PaymentActivityTable;
